import { Component, ElementRef, HostListener, Injector, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { FormType, FormStatusEnum, LoadingOrigin } from 'src/app/common/enumerations/enumerations';
import { CanComponentDeactivate } from 'src/app/common/guards/unsaved-changes.guard';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { FormService } from 'src/app/controls/form-table/form.service';
import { FormsStoreAndListenersService } from 'src/app/controls/form-table/store/forms.store';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { utils } from 'src/app/modules/libs/utils';
import { Form, FormHandler } from 'src/app/services/forms/form';
import { FormVersion } from 'src/app/services/forms/form-version';
import { FormVersionService } from 'src/app/services/forms/form-version.service';
import { SequenceNumber } from 'src/app/services/forms/sequence-number';
import { PpstbSideComponent } from './ppstb-side/ppstb-side.component';
import { PpstbMainComponent } from './ppstb-main/ppstb-main.component';
import { PpstbService } from './ppstb.service';
import { PPSTB } from './ppstb';
import { FormsUpdate } from 'src/app/controls/form-table/store/forms.action';
import { FormHistoryButtonComponent } from 'src/app/controls/form-history/form-history-button/form-history-button.component';

@Component({
  selector: 'app-ppstb',
  templateUrl: './ppstb.component.html',
  styleUrls: ['./ppstb.component.scss']
})
export class PpstbComponent extends BaseComponent implements OnChanges, CanComponentDeactivate {
  @ViewChild("sidenav") sidenav!: MatSidenav;
  @ViewChild("headerTop") headerTop!: ElementRef;
  @ViewChild(PpstbSideComponent) side!: PpstbSideComponent;
  @ViewChild(PpstbMainComponent) main!: PpstbMainComponent;
  @ViewChild(FormHistoryButtonComponent) public showHistory!: FormHistoryButtonComponent;

  disabled: boolean = false;

  resourceRequired: boolean = false;

  filterCtrl = new FormControl();

  errorMessages: string[] = [];
  headerOnTop: boolean = false;

  valueFilter!: string;
  createdOn!: string;

  ///////
  tmpFilter!: string;
  condition!: string;
  filteredFilter: any[] = [];
  currentFilter: any;
  filter: any;

  form?: Form | null;
  forms: Form[] = [];
  forms$!: Observable<Form[]>;
  formsSubs: Subscription = new Subscription;

  loadingMessage!: string;

  showAmendmentToggle: boolean = false;
  showAmendment: boolean = false;
  //////

  buttonsVisible: boolean | null = false;
  private resizeTimer: any;

  constructor(
    public service: PpstbService,
    private formService: FormService,
    private formVersionService: FormVersionService,
    private formStore: FormsStoreAndListenersService,
    protected override injector: Injector
  ) {
    super(injector);
    this.formStore.initStore(FormType.PPSTB);
  }

  ngAfterViewInit(): void {
    this.setHeights();
  }

  ngOnChanges(changes: SimpleChanges): void {

  }

  ngOnInit() {
    this.moduleTitle = 'Personnel Protection System Temporary Bypass';
    this.loadForms();
  }

  loadForms() {
    this.forms$ = this.store.select(state => state.Forms.data);
    this.formsSubs = this.forms$.subscribe(data => {
      if (data?.length) {
        this.forms = data;
        if (this.form) {
          const form = this.forms.find(f => f.id == this.form?.id);
          if (form) {
            this.form = form;
            this.rowSelected(this.form);
          }
        }
      }
    });
  }

  showStatusChangeMessage(form: Form) {
    this.alert.message('formStateChanged',
      [new MessagePlaceholder("{serialNo}", form.sequenceNumber.number), new MessagePlaceholder("{status}", form.formVersion.status.name ?? 'New'),]);
  }

  override ngOnDestroy(): void {
    this.formsSubs?.unsubscribe();
  }

  clear() {
    this.errorMessages = [];
  }

  async create() {
    if (await this.canDeactivate()) {
      const user = this.getCurrentUser();
      const serialNo = await this.formService.getSerialNo(FormType.PPSTB).toPromise();
      if (serialNo && user) {
        this.form = {
          id: 0,
          type: FormType.PPSTB,
          sequenceNumber: new SequenceNumber,
          formVersion: new FormVersion,
          editingById: user.id,
          editingBy: user,
          createdByUser: user,
          createdOn: new Date(),
          date: new Date()
        } as Form;
        this.form.sequenceNumber.number = serialNo?.serialNo;
        this.form.formVersion.statusID == FormStatusEnum.New;
        this.form.formVersion.operatorInitiatingForm = user;
        this.form.formVersion.operatorInitiatingFormID = user.id ?? 0;
        this.form.formVersion.ppstb = new PPSTB;

        this.side.formTable.form = undefined;
        this.setFormDirty();
        this.disabled = false;
      }
    }
  }

  /**
   * Reloads the restored active version after an amendment is canceled and keeps the row selected.
   * @param cancelledForm Form instance captured before cancellation, when supplied by the main form.
   * If no persisted form exists, clears the selection used for a canceled new form.
   */
  async formCancelled(cancelledForm?: Form | null): Promise<void> {
    const cancelledFormID = cancelledForm?.id ?? this.form?.id;
    const cancelledAmendment = cancelledForm?.formVersion?.previousVersionID != null || this.form?.formVersion?.previousVersionID != null;
    this.showAmendment = false;

    if (cancelledFormID && cancelledAmendment) {
      try {
        const activeForm = await this.formService.getById(cancelledFormID).toPromise();
        if (activeForm) {
          // Replace the list row with the restored version so selection and amendment state stay synchronized.
          this.store.dispatch(new FormsUpdate(activeForm.id, activeForm));
          this.form = activeForm;
          this.side.form = activeForm;
          this.side.formTable.form = activeForm;
          this.rowSelected(activeForm);
          return;
        }
      }
      catch {
        this.alert.defaultError();
      }
    }

    this.form = null;
    this.side.form = null;
    this.side.formTable.form = undefined;
  }

  async edit(value: boolean) {
    if (!value && await this.canDeactivate() || value)
      if (this.form?.id) {
        await this.formService.setEditing(this.form.id, value).toPromise();
      }
  }

  /**
   * Selects a PPSTB form and resets amendment/history presentation for the new selection.
   * @param e Selected form, or an empty value when the side-table selection is cleared.
   */
  rowSelected(e: Form | any) {
    if (!e) {
      if (this.showHistory) this.showHistory.value = false;
      this.form = null;
      return;
    }
    if (this.form?.id != e.id)
      this.scrollToTop('sidenav-content');
    this.form = e as Form;
    this.showAmendment = false;
    this.createdOn = moment(this.form?.createdOn).format("MMM-DD-YYYY");
    this.setHeights();
    if (this.form.editingBy && this.form.formVersion.statusID == FormStatusEnum.AmendmentDrafting) this.showAmendment = true;
  }

  /**
   * Allows privileged users to amend a PPSTB until bypass-removal technical approval is signed.
   * @returns Whether the Create Amendment action should be available.
   */
  amendmentEnabled() {
    return (this.form &&
      this.main?.section1?.amendmentSignature?.amendmentEnabled &&
      this.hasPrivilege(this.privilegeEnum.PPSTBRequestAmendment) &&
      this.form.formVersion.statusID != FormStatusEnum.AmendmentDrafting &&
      this.form.formVersion.statusID != FormStatusEnum.AmendmentPending &&
      // Work-completed PPSTBs cannot be amended even if the workflow signature data is stale.
      this.form.formVersion.statusID != FormStatusEnum.WorkCompleted &&
      // Keep amendments available throughout active work until bypass removal receives technical approval.
      !this.form.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 6)?.wfSignatures?.some(s => s.code == 'BYPASS-REMOVAL-TECH-APPR' && s.signed)) ?? false;
  }

  async duplicate() {
    if (await this.canDeactivate()) {
      let form = new Form(FormType.PPSTB);
      const user = this.getCurrentUser();
      const serialNo = await this.formService.getSerialNo(FormType.PPSTB).toPromise();
      if (serialNo && user && this.form) {
        // this.sidePanel.selectedRowIndex = -1;
        form = {
          id: 0,
          type: FormType.PPSTB,
          sequenceNumber: new SequenceNumber,
          formVersion: new FormVersion,
          editingById: user.id,
          createdBy: user.id,
          createdOn: new Date(),
          date: new Date()
        } as Form;
        form.sequenceNumber.number = serialNo?.serialNo;
        form.formVersion.statusID == FormStatusEnum.Draft;
        form.formVersion.operatorInitiatingFormID = user.id ?? 0;
        form.formVersion.requesterID = this.form.formVersion.requesterID;

        const ppstb = utils.cloneDeep(this.form.formVersion.ppstb) as PPSTB;
        ppstb.ppstbAcceleratorLocations.forEach(x => { x.id = 0; x.ppstbID = 0; });
        ppstb.ppstbOtherSystems.forEach(x => { x.id = 0; x.ppstbID = 0; });
        ppstb.ppstbRelatedLinks.forEach(x => { x.formID = 0; x.id = 0; });
        ppstb.ppstbRestrictions.forEach(x => { x.id = 0; x.formID = 0; });
        ppstb.ppstbShutters.forEach(x => { x.ppstbID = 0; x.id = 0; });
        ppstb.ppstbOtherFollowUps = [];
        ppstb.other = false;
        ppstb.modifyInterlock = false;
        ppstb.repairReplaceComponent = false;
        ppstb.id = 0;

        form.formVersion.id = 0;
        form.formVersion.ppstb = ppstb;
        this.setFormDirty();

        this.formService.create(FormHandler.toSave(form))?.subscribe(
          (data: Form) => {
            this.setOpenDocument(data.type, data.id);
            this.form = data;
            this.alert.message('DraftSaved', [
              new MessagePlaceholder("{serialNo}", data.sequenceNumber.number),
            ]);
            this.side.formCreated(this.form);
          },
          (error) => {
            console.error(error);
            this.alert.defaultError();
          }
        );

        this.disabled = false;
      }
    }
  }

  requestAmendment() {
    if (this.form?.wfTableID) {
      const confirm = this.dialog.open(YesNoDialogComponent, {
        width: "500px",
        data: {
          message: this.getMessage('request_amendment').description,
          icon: "warn",
        },
      });

      confirm.afterClosed().subscribe((data) => {
        if (data && this.form) {
          this.formVersionService.newVersion(this.form.formVersion.id).subscribe(async (formVersion: FormVersion) => {
            if (this.form && formVersion && this.main?.section1?.amendmentSignature) {
              this.form.formVersion = utils.cloneDeep(formVersion);
              await this.service.sign(this.form, this.main.section1.amendmentSignature.taskID, this.main.section1.amendmentSignature.amendmentID);
            }
          },
            (error) => {
              console.error(error);
              this.alert.defaultError();
            }
          );
        }
      });
    }
  }

  editAmendment() {
    if (this.form?.id && this.currentUser) {
      this.form.editingBy = this.currentUser;
      this.form.editingById = this.currentUser.id;
      this.store.dispatch(new FormsUpdate(this.form.id, this.form));
      this.showAmendment = true;
      this.formService.setEditing(this.form.id, true).toPromise();
    }
  }

  changeAmendmentToggle(e: any) {
    this.showAmendment = e.checked;
  }

  @HostListener("window:resize")
  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.innerWidth = window.innerWidth;
      if (this.innerWidth < 768) {
        this.isExpanded = false;
      }
    }, 200);
  }

  @HostListener("window:scroll")
  isHeaderOnTop() {
    let elemRec;
    if (this.headerTop?.nativeElement !== undefined) {
      elemRec = this.headerTop.nativeElement.getBoundingClientRect();
      const docViewTop = window.screenTop;
      const elemTop = elemRec.top;
      if (elemTop <= docViewTop) {
        this.headerOnTop = true;
      } else {
        this.headerOnTop = false;
      }
    } else {
      this.headerOnTop = false;
    }
  }

  //////
  changedFilter() { }
  clearFilter() { }
  selectedFilter() { }
  createDisabled() { }
  editDisabled() { }
  action() { }

  applyFilter(e: any) {
    if (!e)
      this.valueFilter = '';
    this.filter = e;
  }

}
